// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Avatar.tsx
 * @input Uses React, HTMLAttributes, ReactNode, useState; useTooltip
 *   (Tooltip hook) for the optional name-on-hover tooltip; useTranslator (i18n)
 * @output Exports Avatar component, AvatarProps, AvatarSize types
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Avatar/Avatar.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Avatar/index.ts (exports if types change)
 * - /apps/storybook/stories/Avatar.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Avatar/ (showcase blocks)
 *
 * Last synced props: alt, fallbackSrc, name, size, src, status, href, as, target, rel, onClick
 */
import { isValidElement, useMemo, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, typographyVars, fontWeightVars, radiusVars } from "../theme/tokens.stylex.js";
import { AvatarSizeContext } from "./AvatarSizeContext.js";
import { useAvatarGroup } from "../AvatarGroup/AvatarGroupContext.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { firstCharacter } from "../utils/characters.js";
import { focusOutlineProps } from "../utils/focusOutline.stylex.js";
import { useTooltip } from "../Tooltip/useTooltip.js";
import { useDevWarning } from "../hooks/useDevWarning.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { useTranslator } from "../i18n/index.js";

/**
 * The offset ratio for positioning elements on a circle's edge at 45°.
 *
 * For a square with side length S containing an inscribed circle of diameter S,
 * a diagonal line from corner to corner intersects the circle at:
 *   x = S/2 × (1 ± 1/√2)
 *
 * The distance from the corner to this intersection point (along each axis) is:
 *   S/2 × (1 - 1/√2) ≈ 0.146S
 *
 * This constant represents that ratio: (1 - 1/√2) / 2 ≈ 0.146
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const CIRCLE_EDGE_OFFSET_RATIO = (1 - 1 / Math.SQRT2) / 2;

/**
 * The ratio of font size to avatar size for initials.
 *
 * At 40%, two-letter initials fit comfortably within the circle with adequate
 * padding. This ratio provides good legibility across all avatar sizes:
 *   - 24px avatar → 9.6px font
 *   - 48px avatar → 19.2px font
 *   - 128px avatar → 51.2px font
 */
const INITIALS_FONT_SIZE_RATIO = 0.4;

/**
 * Named size options.
 *
 * Avatar uses the same abbreviated scale as Icon (`xsm`/`sm`/`md`/`lg`/`xl`),
 * but the values are larger because avatars align with media rather than
 * glyphs. The tiers follow the standard avatar size scale.
 */

/**
 * Numeric size options (in pixels)
 */

/**
 * Avatar size - can be a named size or a specific pixel value
 */

/**
 * Resolves named sizes to their numeric pixel values
 */
export function resolveSize(size) {
  if (typeof size === 'number') {
    return size;
  }
  switch (size) {
    case 'xsm':
      return 20;
    case 'sm':
      return 24;
    case 'md':
      return 36;
    case 'lg':
      return 48;
    case 'xl':
      return 128;
  }
}

/**
 * Base styles for the avatar
 * Uses a wrapper/content structure so status isn't clipped by overflow:hidden
 */
const styles = {
  wrapper: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kmuXW: "x2lah0s",
    kaIpWk: "xjspbzw",
    $$css: true
  },
  fallback: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kWkggS: "x17x4s8c",
    kMwMTN: "xv1l7n4",
    kMv6JI: "x9ynric",
    k63SB2: "x1e4wzip",
    kP9fke: "xtvhhri",
    $$css: true
  },
  status: {
    kVAEAm: "x10l6tqk",
    $$css: true
  },
  interactive: {
    kysU6D: "xjyslct",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1heor9g",
    kVVagm: "xln7xf2",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk",
    kaIpWk: "xjspbzw",
    $$css: true
  }
};

/**
 * Dynamic styles that depend on the avatar size
 */
const _temp = {
  kGuDYH: "xdmh292",
  "$$css": true
};
const _temp2 = {
  krVfgx: "x1nqzi6q",
  kt4wiu: "xqxgn94",
  k3aq6I: "x4v1no5 " + "xqgnvz5",
  "$$css": true
};
const dynamicStyles = {
  size: size => [{
    kzqmXN: size != null ? "x5lhr3w" : size,
    kZKoxP: size != null ? "x16ye13r" : size,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }],
  fontSize: size => [_temp, {
    "--x-fontSize": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${size * INITIALS_FONT_SIZE_RATIO}px`)
  }],
  statusPosition: size => [_temp2, {
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size * CIRCLE_EDGE_OFFSET_RATIO),
    "--x-insetInlineEnd": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size * CIRCLE_EDGE_OFFSET_RATIO)
  }]
};
const BORDER_WIDTH = 2;
const groupStyles = {
  ring: {
    kaIpWk: "xjspbzw",
    kMzoRj: "xdh2fpr",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1touxvs",
    kWkggS: "x10xzikg",
    kB7OPa: "x1afcbsf",
    $$css: true
  },
  overlap: {
    keTefX: "x1sik18w",
    $$css: true
  }
};
const _temp3 = {
  "--_avatar-group-overlap": "xlz5hwt",
  "$$css": true
};
const groupDynamicStyles = {
  overlap: offset => [_temp3, {
    "--x---_avatar-group-overlap": `${offset}px` != null ? `${offset}px` : undefined
  }]
};
/**
 * Generates initials from a name string.
 * Takes the first letter of the first two words.
 * @example
 * ```
 * getInitials('John Doe')
 * getInitials('Alice')
 * ```
 */
function getInitials(name) {
  const words = name.trim().split(/\s+/);
  if (words.length === 0) {
    return '';
  }
  if (words.length === 1) {
    return firstCharacter(words[0]).toUpperCase();
  }
  return (firstCharacter(words[0]) + firstCharacter(words[words.length - 1])).toUpperCase();
}

/**
 * Reads the accessible status label off the `status` element, when it
 * exposes one. `AvatarStatusDot`'s `label` prop is the canonical source,
 * but any custom status element with a string `label` prop participates.
 *
 * The avatar root is `role="img"`, which prunes ALL descendant semantics
 * from the accessibility tree — a label inside the status subtree is never
 * announced on its own. Composing it into the avatar's own accessible name
 * is the only way the status reaches assistive tech (WCAG 4.1.2).
 */
function getStatusLabel(status) {
  if (! /*#__PURE__*/isValidElement(status)) {
    return undefined;
  }
  const {
    label
  } = status.props;
  return typeof label === 'string' && label !== '' ? label : undefined;
}

/**
 * Default person icon SVG for when no image or name is provided
 */
function DefaultIcon({
  size
}) {
  return /*#__PURE__*/_jsx("svg", {
    width: size * 0.6,
    height: size * 0.6,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    })
  });
}

/**
 * Avatar component for displaying user profile pictures.
 *
 * Displays an image when available, falling back to initials derived from
 * the name prop, or a generic person icon if neither is provided.
 *
 * @example
 * ```
 * <Avatar src="/user.jpg" name="John Doe" />
 * <Avatar name="Jane Smith" size="xl" />
 * <Avatar src="/user.jpg" status={<AvatarStatusDot variant="success" label="Online" />} />
 * <Avatar name="jsmith" tooltip="Jane Smith, Staff Engineer" />
 * <Avatar name="Jane" tooltip={false} />
 * <Avatar src="/user.jpg" name="John Doe" href="/users/john" />
 * <Avatar src="/user.jpg" name="John Doe" onClick={() => openProfile()} />
 * ```
 */
export function Avatar({
  alt,
  'data-testid': testId,
  fallbackSrc,
  name,
  size = 'md',
  src,
  status,
  tooltip = true,
  href,
  as,
  target,
  rel,
  onClick,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  // Track the exact src that failed (rather than a boolean) so a changed
  // src/fallbackSrc gets a fresh load attempt instead of the stale error.
  const [erroredSrc, setErroredSrc] = useState(undefined);
  const [erroredFallbackSrc, setErroredFallbackSrc] = useState(undefined);
  const showImage = src && erroredSrc !== src;
  const showFallbackImage = !showImage && fallbackSrc && erroredFallbackSrc !== fallbackSrc;
  // A whitespace-only string carries no identity. Without this it produces no
  // initials (getInitials trims to nothing) and no default icon (a space is
  // truthy), leaving an empty plate behind a blank accessible name.
  const meaningfulName = name?.trim() ? name : undefined;
  const meaningfulAlt = alt?.trim() ? alt : undefined;
  const showInitials = !showImage && !showFallbackImage && meaningfulName;
  const showIcon = !showImage && !showFallbackImage && !meaningfulName;

  // A meaningful accessible name comes from `alt`/`name`, composed with the
  // status element's `label` when one is present ("Jane Doe, Online") — the
  // `role="img"` root prunes descendant semantics, so surfacing the label in
  // the avatar's own name is the only way assistive tech can reach the
  // status (WCAG 4.1.2). A labelled status alone is also meaningful. With
  // neither a name nor a labelled status, the avatar is decorative — expose
  // it as `presentation`/`aria-hidden` rather than announcing a meaningless
  // generic "Avatar" (obs-9).
  const t = useTranslator();
  const nameLabel = meaningfulAlt || meaningfulName;
  const statusLabel = getStatusLabel(status);
  const accessibleName = nameLabel && statusLabel ? t('@astryx.avatar.nameWithStatus', {
    name: nameLabel,
    status: statusLabel
  }) : nameLabel || statusLabel;
  const isDecorative = !accessibleName;
  const avatarGroup = useAvatarGroup();
  const resolvedSize = avatarGroup?.size ?? size;
  const numericSize = useMemo(() => resolveSize(resolvedSize), [resolvedSize]);

  // Resolve the tooltip content:
  // - `false`            → no tooltip
  // - a string           → that string
  // - `true` / omitted   → the `name` (a whitespace-only name yields nothing)
  // Note: the *visible* tooltip prefers `name` (not `alt`); the *accessible
  // name* on the root still uses `alt || name` above, independent of this.
  const tooltipContent = tooltip === false ? undefined : typeof tooltip === 'string' ? tooltip : meaningfulName;
  const trimmedTooltip = tooltipContent?.trim();
  const showTooltip = trimmedTooltip != null && trimmedTooltip !== '';
  // Whether the tooltip text is a consumer-authored override (a custom string)
  // rather than the default name. A custom description is worth wiring to
  // `aria-describedby` (it adds information, matching Button); the default name
  // tooltip is visual-only — its text duplicates the root `aria-label`, so
  // describing it too would double-announce the same name (OQ-4).
  const isCustomTooltip = typeof tooltip === 'string';

  // Own the name tooltip via the Tooltip hook (the Button pattern), which
  // returns `describedBy` as a value we choose whether to apply — the only way
  // to satisfy the per-case aria-describedby rule (default name: none; custom
  // string: describe) without editing Tooltip. `focusTrigger: 'auto'` shows the
  // tooltip on keyboard focus once the root is focusable (natively for the
  // interactive <a>/<button>, or via an explicit tab stop on the static div).
  const tooltipHook = useTooltip({
    placement: 'above',
    isEnabled: showTooltip
  });
  // The tooltip ref attaches to whichever root element renders (static or
  // interactive), so the tooltip works for link/button avatars too.
  const rootRef = mergeRefs(ref, showTooltip ? tooltipHook.ref : undefined);
  const describedByProp = showTooltip && isCustomTooltip ? {
    'aria-describedby': [props['aria-describedby'], tooltipHook.describedBy].filter(Boolean).join(' ') || undefined
  } : null;

  // Element-swap trichotomy, copied from Button: `href` renders a link,
  // otherwise `onClick` renders a `<button>`, otherwise today's static element
  // is unchanged (the non-breaking default).
  const renderAsLink = href != null;
  const renderAsButton = !renderAsLink && onClick != null;
  const isInteractive = renderAsLink || renderAsButton;
  const LinkComponent = useLinkComponent(as);

  // An interactive control with no accessible name is an unacceptable control
  // name. `useDevWarning` is the shared guardrail: it warns once per mount
  // rather than on every render, and compiles out of production builds.
  useDevWarning('Avatar', 'an interactive avatar (with `href` or `onClick`) needs a meaningful ' + 'accessible name. Pass `alt` or `name`.', isInteractive && !accessibleName);

  // The inner visuals are identical across the static and interactive variants.
  const visualContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 x6s0dn4 xl56j7k xh8yej3 x5yr21d xjspbzw xb3r6kr x87ps6o"
      },
      children: [showImage && /*#__PURE__*/_jsx("img", {
        src: src,
        alt: "",
        onError: () => setErroredSrc(src),
        ...{
          className: "xh8yej3 x5yr21d xl1xv1r"
        }
      }), showFallbackImage && /*#__PURE__*/_jsx("img", {
        src: fallbackSrc,
        alt: "",
        onError: () => setErroredFallbackSrc(fallbackSrc),
        ...{
          className: "xh8yej3 x5yr21d xl1xv1r"
        }
      }), showInitials && /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('avatar-fallback', {
          size: resolvedSize
        }), stylex.props(styles.fallback, dynamicStyles.fontSize(numericSize))),
        children: getInitials(meaningfulName)
      }), showIcon && /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('avatar-fallback', {
          size: resolvedSize
        }), {
          className: "x78zum5 x6s0dn4 xl56j7k xh8yej3 x5yr21d x17x4s8c xv1l7n4 x9ynric x1e4wzip xtvhhri"
        }),
        children: /*#__PURE__*/_jsx(DefaultIcon, {
          size: numericSize
        })
      })]
    }), status && /*#__PURE__*/_jsx("div", {
      ...stylex.props(styles.status, dynamicStyles.statusPosition(numericSize)),
      children: status
    })]
  });

  // Shared StyleX + theme props for the root element in every variant. The
  // group ring/overlap, the interactive focus-visible ring, and the
  // tooltip tab-stop focus ring all live here so the interactive
  // `<a>`/`<button>` and the static `<div>` carry the exact same box.
  const rootStylexProps = mergeProps(themeProps('avatar', {
    size: resolvedSize
  }), focusOutlineProps.focusVisible(styles.wrapper, dynamicStyles.size(numericSize), isInteractive && styles.interactive, avatarGroup && groupStyles.ring, avatarGroup && groupStyles.overlap, avatarGroup && groupDynamicStyles.overlap(-avatarGroup.overlap), xstyle), className, style);
  let rootElement;

  // `props` is typed for the default `<div>` root (its event handlers are
  // HTMLDivElement-typed). The interactive branches render an `<a>`/`<button>`,
  // so the passthrough props are re-typed to the generic element here — the
  // avatar's own handlers (onClick) are declared on HTMLElement and stay typed.
  const interactivePassthrough = props;
  if (renderAsLink) {
    // The rendered link carries the `data-avatar-item` marker so AvatarGroup's
    // roving focus (which selects on `[data-avatar-item]`, not a tag/role) picks
    // it up while ignoring nested buttons in a custom status/badge slot.
    rootElement = /*#__PURE__*/_jsx(LinkComponent, {
      ...interactivePassthrough,
      ...describedByProp,
      ref: rootRef,
      href: href,
      target: target,
      rel: rel,
      "aria-label": accessibleName,
      "data-avatar-item": "",
      "data-testid": testId,
      onClick: onClick,
      ...rootStylexProps,
      children: visualContent
    });
  } else if (renderAsButton) {
    rootElement = /*#__PURE__*/_jsx("button", {
      ...interactivePassthrough,
      ...describedByProp,
      ref: rootRef,
      type: "button",
      "aria-label": accessibleName,
      "data-avatar-item": "",
      "data-testid": testId,
      onClick: onClick,
      ...rootStylexProps,
      children: visualContent
    });
  } else {
    rootElement = /*#__PURE__*/_jsx("div", {
      ...props,
      ref: rootRef,
      role: isDecorative ? 'presentation' : 'img',
      "aria-label": isDecorative ? undefined : accessibleName,
      "aria-hidden": isDecorative || undefined
      // The root is a div[role="img"], not natively focusable. When a name
      // tooltip is active, add a tab stop so keyboard users can reveal it
      // (WCAG 1.4.13 / 2.1.1) — matching Timestamp/Button. Suppressed inside
      // an AvatarGroup, which owns a single roving tab stop for its members.
      ,
      tabIndex: showTooltip && !avatarGroup ? 0 : undefined,
      "data-testid": testId,
      ...describedByProp,
      ...rootStylexProps,
      children: visualContent
    });
  }
  const avatarElement = /*#__PURE__*/_jsx(AvatarSizeContext, {
    value: numericSize,
    children: rootElement
  });

  // Always return the same structure so the avatar keeps its position in the
  // React tree regardless of the tooltip flag — toggling it must not remount
  // the avatar subtree (and lose image-load state). The tooltip is a sibling
  // (no wrapper DOM); the hook's ref is already on the root via `rootRef`.
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [avatarElement, showTooltip ? tooltipHook.renderTooltip(trimmedTooltip) : null]
  });
}
Avatar.displayName = 'Avatar';